// This route exists so that we can check if the user is fully setup and only
// then, he can go through towards the app. Generally, this route is used after a signup.
import { useAuth } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { BlymLoading } from '../../components/loading';
import { InternalServerError, NotFoundError } from '../../error';
import { trackError } from '../../error/tracking';
import { getLogger } from '../../logger';
import { captureException } from '../../logger/capture-exception';
import { getQueryClient } from '../../sdk/request';
import { buildUserData, isGTMLoaded } from '../../services/gtm';
import { PostHogEvents, capturePostHogEvent } from '../../services/posthog';
import {
  createUserImperative,
  fetchAuthUserImperative,
  fetchUserOrganizationsImperative,
} from '../../services/storage';
import { UIKey } from '../../services/storage/ui-key-value';
import { tGlobal } from '../../services/translation';
import { getUtmKey } from '../../shared/utm';

const MAX_RETRIES = 10;
const RETRY_DEBOUNCE_MS = 1000;

async function checkUser(getToken: ReturnType<typeof useAuth>['getToken']): Promise<boolean> {
  if (getToken == null) {
    return false;
  }

  const token = await getToken();
  if (token == null || token.length === 0) {
    throw new InternalServerError('unable to retrieve token');
  }

  const queryClient = getQueryClient();
  const { created } = await createUserImperative(queryClient, token, {
    signupSource: getUtmKey(UIKey.UTM_SOURCE),
    signupMedium: getUtmKey(UIKey.UTM_MEDIUM),
    signupCampaign: getUtmKey(UIKey.UTM_CAMPAIGN),
    signupTerm: getUtmKey(UIKey.UTM_TERM),
    signupGclid: getUtmKey(UIKey.GCLID),
    signupFbclid: getUtmKey(UIKey.FBCLID),
    signupLiFatId: getUtmKey(UIKey.LI_FAT_ID),
  });

  return created;
}

async function checkUserWithRetries(
  getToken: ReturnType<typeof useAuth>['getToken'],
  retryCount: number
): Promise<boolean> {
  for (let i = 0; i < retryCount; i += 1) {
    let created = false;
    let allDone = false;
    await checkUser(getToken)
      .then(function (wasCreated) {
        created = wasCreated;
        allDone = true;
      })
      .catch(async function (err) {
        if (err instanceof NotFoundError && i < retryCount - 1) {
          await new Promise(function (resolve) {
            setTimeout(function () {
              resolve(undefined);
            }, RETRY_DEBOUNCE_MS);
          });

          getLogger().info('[Wait user] going for another retry', { retryCount: i + 1 });
          return false;
        }

        throw err;
      });

    if (allDone) {
      return created;
    }
  }

  return false;
}

async function handleGtm(isNewUser: boolean) {
  if (!isNewUser) {
    return;
  }

  if (!isGTMLoaded() || window.dataLayer == null) {
    getLogger().warn('[Wait user] data layer is not initialized');
    throw new Error('data layer is not initialized');
  }

  const queryClient = getQueryClient();
  const user = await fetchAuthUserImperative(queryClient);
  let organizationId: string | undefined = user?.selectedOrganizationId;

  if (organizationId == null || organizationId.length === 0) {
    try {
      const orgUsers = await fetchUserOrganizationsImperative(queryClient);
      organizationId = orgUsers[0]?.organizationId;
    } catch (err) {
      getLogger().warn('[Wait user] unable to fetch organizations', { err });
      captureException(
        err as Error,
        {
          file: 'wait-user.tsx',
          method: 'checkUserWithRetries',
          infoMessage: 'unable to fetch organizations for signup event',
        },
        trackError
      );
    }
  }

  window.dataLayer.push({
    event: 'user_signup',
    organizationId: organizationId,
    // eslint-disable-next-line camelcase
    user_data: user != null ? buildUserData(user) : undefined,
  });
}

export const Route = createFileRoute('/auth/wait-user')({
  // eslint-disable-next-line no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { getToken } = useAuth();
  const hasStarted = useRef(false);

  useEffect(
    function () {
      if (hasStarted.current) {
        return;
      }
      hasStarted.current = true;

      checkUserWithRetries(getToken, MAX_RETRIES)
        .then(async function (isNewUser) {
          try {
            await handleGtm(isNewUser);
          } catch (err) {
            getLogger().warn('[Wait user] unable to go through with the GTM', { err });
            captureException(
              err,
              {
                file: 'wait-user.tsx',
                method: 'handleGtm',
                infoMessage: 'unable to go through with the GTM',
              },
              trackError
            );
          }

          if (isNewUser) {
            capturePostHogEvent(PostHogEvents.SIGNUP_COMPLETED);
          }

          window.location.href = '/app';
        })
        .catch(function (err: unknown) {
          getLogger().warn('[Wait user] unable to check user', { err });
          captureException(
            err,
            {
              file: 'wait-user.tsx',
              method: 'checkUser',
              infoMessage: 'unable to check user',
            },
            trackError
          );
        });
    },
    [getToken]
  );

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-white">
      <BlymLoading />
      <p className="font-display-2">{tGlobal('loadingWorkspace')}</p>
    </div>
  );
}
